/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useEffect, useMemo } from "react";
import {
  PageLayout,
  InstantSearch,
  SimpleToggle,
  TableSkeleton,
  StandardTable,
} from "@components";
import { useAllUsers } from "../../hooks";
import { ITableRow, Timestamp } from "../../types";
import { useTranslation } from "react-i18next";

export const UserPage: FC = function () {
  const [isLoading, setLoading] = useState(true);
  const [tableRows, setTableRows] = useState([] as ITableRow[]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hideApiKeys, setHideApiKeys] = useState(true);
  const { t } = useTranslation(["common", "user,"]);
  const query = useAllUsers();

  const isQueryLoading = useMemo(
    () => query.isLoading || query.isRefetching,
    [query],
  );

  const queryData = useMemo(() => query.data ?? [], [query]);

  // Function to detect if an email is an API key (UUID format with @domain.logibud.com)
  const isApiKey = (email: string): boolean => {
    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}@.*\.logibud\.com$/i;
    return uuidPattern.test(email);
  };

  useEffect(() => {
    if (!isQueryLoading) {
      setLoading(false);

      // Filter users based on search term and API key toggle
      let filteredUsers = queryData.filter((user) => {
        // Filter by search term (email)
        const matchesSearch = user.email
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

        // Filter by API key toggle
        const isUserApiKey = isApiKey(user.email);
        const shouldShowUser = hideApiKeys ? !isUserApiKey : true;

        return matchesSearch && shouldShowUser;
      });

      // Sort users by creation date (newest first)
      const sortedUsers = [...filteredUsers].sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return b.createdAt.seconds - a.createdAt.seconds;
      });

      setTableRows(
        sortedUsers.map((user) => {
          return {
            id: user.id,
            cells: [
              {
                id: "email",
                children: (
                  <div
                    className="max-w-96 overflow-hidden text-ellipsis whitespace-nowrap"
                    title={user.email}
                  >
                    {user.email}
                  </div>
                ),
                showOnSmallScreen: true,
              },
              {
                id: "emailVerification",
                children: (
                  <div className="flex items-center">
                    {user.emailVerified ? (
                      <div className="mr-2 h-2.5 w-2.5 rounded-full bg-green-400"></div>
                    ) : (
                      <div className="mr-2 h-2.5 w-2.5 rounded-full bg-red-400"></div>
                    )}
                    {user.emailVerified ? t("common:yes") : t("common:no")}
                  </div>
                ),
              },
              {
                id: "createdAt",
                children: Timestamp.toDisplayDate(user.createdAt),
              },
            ],
          };
        }),
      );
    }
  }, [isQueryLoading, queryData, t, searchTerm, hideApiKeys]);

  return (
    <PageLayout>
      <div className="flex flex-col">
        <div className="overflow-x-auto w-full">
          <div className="inline-block min-w-full align-middle">
            <div className="pb-4">
              <h1 className="text-xl font-semibold text-text sm:text-2xl">
                {t("user:admin_title")}
              </h1>
              <p className="text-secondary text-base pt-2 pb-4 dark:text-secondary">
                {t("user:admin_description")}
              </p>

              {/* Search and Filter Controls */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                <div className="w-full sm:w-1/2 min-w-0">
                  <InstantSearch
                    placeholder="Search by email..."
                    value={searchTerm}
                    onChange={setSearchTerm}
                  />
                </div>
                <div className="flex-shrink-0">
                  <SimpleToggle
                    label="Hide API Keys"
                    isEnabled={hideApiKeys}
                    onToggle={() => setHideApiKeys(!hideApiKeys)}
                  />
                </div>
              </div>
            </div>
            <div className="overflow-hidden shadow">
              {isLoading ? (
                <TableSkeleton />
              ) : (
                <StandardTable
                  header={[
                    {
                      id: "email",
                      children: t("user:users.email"),
                      showOnSmallScreen: true,
                    },
                    {
                      id: "emailVerification",
                      children: "Verifisert",
                    },
                    {
                      id: "createdAt",
                      children: t("common:created_at"),
                    },
                  ]}
                  rows={tableRows}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
